import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

const formatMetric = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const isVideoUrl = (url = '') => /\.(mp4|mov|webm|ogg)(\?|$)/i.test(url);

const statItems = (media) => [
  { label: 'Reach', value: formatMetric(media?.reach) },
  { label: 'Engagement', value: formatMetric(media?.engagement) },
  { label: 'Impressions', value: formatMetric(media?.impressions) },
  { label: 'Likes', value: formatMetric(media?.likes) },
  { label: 'Comments', value: formatMetric(media?.comments) },
  { label: 'Saves', value: formatMetric(media?.saves) },
  { label: 'Shares', value: formatMetric(media?.shares) },
  { label: 'Plays', value: formatMetric(media?.plays) },
  { label: 'Video Views', value: formatMetric(media?.video_views) },
];

const MediaAnalyticsModal = ({ open, media, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '18px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box className="profile-media-modal-shell">
          <IconButton className="profile-media-modal-close" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>

          <Box className="profile-media-modal-preview">
            {isVideoUrl(media?.media_url) ? (
              <Box
                component="video"
                src={media?.media_url}
                controls
                className="profile-media-modal-asset"
              />
            ) : (
              <Box
                component="img"
                src={media?.media_url || media?.thumbnail_url}
                alt={media?.caption || 'Media preview'}
                className="profile-media-modal-asset"
              />
            )}
          </Box>

          <Box className="profile-media-modal-details">
            <Typography variant="h5" className="profile-analytics-section-title">
              Media Analytics
            </Typography>
            <Typography variant="body2" className="profile-analytics-helper">
              Performance summary for the selected Instagram media.
            </Typography>

            <Box className="profile-media-modal-stats">
              {statItems(media).map((item) => (
                <Box key={item.label} className="profile-media-modal-stat">
                  <Typography variant="body2" className="profile-media-modal-stat-label">
                    {item.label}
                  </Typography>
                  <Typography variant="h6" className="profile-media-modal-stat-value">
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box className="profile-media-modal-meta">
              <Typography variant="subtitle2" className="profile-media-modal-meta-title">
                Caption
              </Typography>
              <Typography variant="body2" className="profile-media-modal-caption">
                {media?.caption || 'No caption available.'}
              </Typography>
            </Box>

            <Box className="profile-media-modal-meta">
              <Typography variant="subtitle2" className="profile-media-modal-meta-title">
                Media URL
              </Typography>
              {media?.media_url ? (
                <Link
                  href={media.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  className="profile-media-modal-link"
                >
                  Open Media URL <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                </Link>
              ) : (
                <Typography variant="body2" className="profile-analytics-helper">
                  No media URL available.
                </Typography>
              )}
            </Box>

            <Box className="profile-media-modal-meta">
              <Typography variant="subtitle2" className="profile-media-modal-meta-title">
                Thumbnail URL
              </Typography>
              {media?.thumbnail_url ? (
                <Link
                  href={media.thumbnail_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  className="profile-media-modal-link"
                >
                  Open Thumbnail URL <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                </Link>
              ) : (
                <Typography variant="body2" className="profile-analytics-helper">
                  No thumbnail URL available.
                </Typography>
              )}
            </Box>

            <Box className="profile-media-modal-meta">
              <Typography variant="subtitle2" className="profile-media-modal-meta-title">
                Permalink
              </Typography>
              {media?.permalink ? (
                <Link
                  href={media.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  className="profile-media-modal-link"
                >
                  Open Post <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                </Link>
              ) : (
                <Typography variant="body2" className="profile-analytics-helper">
                  No permalink available.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MediaAnalyticsModal;
